import React, { useMemo } from 'react'

import { environment } from 'config'

import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import { t } from 'src/translations/src'

import Button from 'shared/components/common/button'
import Image from 'shared/components/common/Image'
import Link from 'shared/components/common/link'
import { useModal } from 'shared/components/common/modal'
import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay
} from 'shared/components/common/modal/Modal'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'

import { useFreeDeliveryChannelEnabled } from 'public/components/default_template/offers/useFreeDeliveryOffer'
import { useCompletedOrder } from 'public/components/online_ordering/OrderContext'

import { useBrandedAppPromoStatus } from '../../app_promo/useAppPromo'
import { getMobileOS } from './getMobileOS'

// shared-static-assets, pinned to the commit that added these two so a later asset
// change cannot alter this page without a deliberate bump.
export const DELIVERY_TRACKING_MAP_SRC =
  'https://cdn.toasttab.com/static/114b82a2f1b39b343db90f285800238a5845a4ca/projects/booking/delivery-tracking-map.svg'
export const TOAST_LOCAL_APP_ICON_SRC =
  'https://cdn.toasttab.com/static/114b82a2f1b39b343db90f285800238a5845a4ca/projects/booking/toast-local-app-icon.svg'

const IS_PROD = environment === 'production'
// Both QR codes ship to every environment, so the prod origin serves either one.
export const TOAST_LOCAL_QR_CODE_SRC = IS_PROD
  ? 'https://toast.app/images/prod-app-download-qr-code.svg'
  : 'https://toast.app/images/preprod-app-download-qr-code.svg'
export const TOAST_LOCAL_APP_DOWNLOAD_LINK = IS_PROD
  ? 'https://toasttab.com/r/lbt/bookingAppDownload'
  : 'https://preprod.eng.toasttab.com/r/lbt/bookingAppDownload'
const GET_APP_TRIGGER_ID = 'toast-local-app-cta-trigger'
const CTA_SOURCE = 'Order confirmation'
const QR_MODAL_HEADER_ID = 'toast-local-app-cta-modal-header'
const QR_MODAL_GOT_IT_ID = 'toast-local-app-cta-modal-got-it'

export const ToastLocalAppCTASection = () => {
  const { completedOrder } = useCompletedOrder()
  const { isOpen, onOpen, onClose } = useModal()
  const mobileOS = useMemo(getMobileOS, [])
  const { track } = useThrottledTracker()

  const isDeliveryOrder =
    completedOrder?.diningOptionBehavior === DiningOptionBehavior.Delivery

  // This section only renders for the toastLocal variant.
  const trackGetApp = (medium: 'link' | 'qr') =>
    track(TrackingEvents.TOAST_LOCAL_APP_CTA_CLICKED, {
      source: CTA_SOURCE,
      variant: 'toastLocal' as AppCTAVariant,
      medium,
      deviceType: mobileOS ?? 'desktop'
    })

  const onGetAppQrClick = () => {
    trackGetApp('qr')
    onOpen()
  }

  return (
    <div
      className='orderConfirmationSection toastLocalAppCtaSection pdfHide'
      data-testid='toast-local-app-cta-section'
    >
      <Image
        src={
          isDeliveryOrder ? DELIVERY_TRACKING_MAP_SRC : TOAST_LOCAL_APP_ICON_SRC
        }
        alt={t(
          isDeliveryOrder
            ? 'order-confirmation.toast-local-app-cta.delivery-map-alt'
            : 'order-confirmation.toast-local-app-cta.app-logo-alt'
        )}
        className={isDeliveryOrder ? 'appLogo deliveryMap' : 'appLogo'}
      />
      <div className='ctaBody'>
        <p>
          {isDeliveryOrder
            ? t('order-confirmation.toast-local-app-cta.delivery')
            : t('order-confirmation.toast-local-app-cta.pickup')}
        </p>
        {mobileOS ? (
          <Link
            className='orderConfirmationButton getAppButton'
            href={TOAST_LOCAL_APP_DOWNLOAD_LINK}
            testId='toast-local-app-cta-link'
            onClick={() => trackGetApp('link')}
          >
            {t('order-confirmation.toast-local-app-cta.get-app')}
          </Link>
        ) : (
          <Button
            id={GET_APP_TRIGGER_ID}
            className='orderConfirmationButton getAppButton'
            testId='toast-local-app-cta-button'
            onClick={onGetAppQrClick}
          >
            {t('order-confirmation.toast-local-app-cta.get-app')}
          </Button>
        )}
      </div>
      {!mobileOS && (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          testId='toast-local-app-qr-modal'
        >
          <ModalOverlay testId='toast-local-app-qr-overlay' />
          <ModalContent
            contentClassName='toastLocalAppQrModal'
            ariaLabelledBy={QR_MODAL_HEADER_ID}
            initialFocusElementId={QR_MODAL_GOT_IT_ID}
            returnFocusElementId={GET_APP_TRIGGER_ID}
          >
            <div className='modalHeader'>
              <ModalCloseButton />
            </div>
            <div className='modalBody'>
              <h2 id={QR_MODAL_HEADER_ID}>
                {t('order-confirmation.toast-local-app-cta.modal-header')}
              </h2>
              <p>
                {t('order-confirmation.toast-local-app-cta.modal-subtitle')}
              </p>
              <img
                className='qrCode'
                src={TOAST_LOCAL_QR_CODE_SRC}
                alt={t('order-confirmation.toast-local-app-cta.qr-code-alt')}
              />
              <Button
                id={QR_MODAL_GOT_IT_ID}
                className='gotItButton'
                testId='toast-local-app-qr-got-it'
                onClick={onClose}
              >
                {t('order-confirmation.toast-local-app-cta.got-it')}
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}
    </div>
  )
}

export type AppCTAVariant = 'toastLocal' | 'default' | 'pending'

/** Decides which of the two app CTAs the confirmation page is allowed to show. */
export const useAppCTAVariant = (): AppCTAVariant => {
  const { restaurant, ooRestaurant } = useRestaurant()
  const channelEnabled = useFreeDeliveryChannelEnabled()
  // Same arguments as AppCTASection so the two never disagree about whether the
  // restaurant has an app of its own, plus the gate so no storefront this CTA can
  // never appear on pays for the query.
  const { appPromoData, loading } = useBrandedAppPromoStatus({
    skip: !channelEnabled || !!restaurant.content?.navConfig?.disableAppPromo
  })

  // The two CTAs live in different columns, so answering before the branded app
  // is known would move the page after it had already painted.
  if (loading) {
    return 'pending'
  }

  // consumerCTAEnabled is the merchant's opt out of Toast app CTAs, which this is.
  return channelEnabled && !appPromoData && !!ooRestaurant?.consumerCTAEnabled
    ? 'toastLocal'
    : 'default'
}
